<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useAuthStore } from "@/stores/auth";
import { APP_VERSION } from "@/constants";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCalendarAlt,
  faSignInAlt,
  faUserPlus,
  faChevronDown,
  faTimes,
  faUser,
  faIdCard,
  faCalendarCheck,
  faSignOutAlt,
  faBars,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Añadir los iconos a la biblioteca
library.add(
  faCalendarAlt,
  faSignInAlt,
  faUserPlus,
  faChevronDown,
  faTimes,
  faUser,
  faIdCard,
  faCalendarCheck,
  faSignOutAlt,
  faBars
);

// Inicializar el store de autenticación y el router
const authStore = useAuthStore();

// Usar la autenticación real en lugar de simular
const isLoggedIn = computed(() => authStore.isAuthenticated);
const userData = computed(() => authStore.getUser);

const isMenuOpen = ref(false);
const isScrolled = ref(false);

// Control de scroll para cambiar estilos del header
const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

// Alternar menú móvil
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  // Prevenir scroll cuando el menú está abierto
  document.body.style.overflow = isMenuOpen.value ? "hidden" : "";
};

// Cerrar menú al navegar
const closeMenu = () => {
  isMenuOpen.value = false;
  document.body.style.overflow = "";
};

// Función para cerrar sesión usando el store de autenticación
const logout = async () => {
  try {
    await authStore.logout();
    closeMenu();
    // El redirect ya está incluido en el método logout() del store
  } catch (error) {
    console.error("Error al cerrar sesión:", error);
  }
};

// Configurar y limpiar listeners de eventos
onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  document.body.style.overflow = "";
});

// Versión de la aplicación
const appVersion = APP_VERSION;
</script>

<template>
  <header
    class="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-white text-black"
    :class="isScrolled ? 'shadow-md py-2' : 'py-4'"
  >
    <div
      class="container mx-auto px-4 md:px-6 flex justify-between items-center"
    >
      <div class="flex-shrink-0">
        <router-link to="/" class="block">
          <img
            src="@/assets/img/logo-main-black.png"
            alt="Hike Society"
            class="h-8 md:h-10"
          />
        </router-link>
      </div>

      <nav class="hidden lg:flex items-center space-x-8">
        <template v-if="!isLoggedIn">
          <router-link
            to="/login"
            class="font-medium hover:text-[#99b642] transition-colors text-black"
          >
            <font-awesome-icon
              :icon="['fas', 'sign-in-alt']"
              class="mr-2"
            />Iniciar sesión
          </router-link>

          <router-link
            to="/register"
            class="px-4 py-2 rounded-full bg-[#99b642] text-white font-medium hover:bg-[#89a438] transition-colors"
          >
            <font-awesome-icon
              :icon="['fas', 'user-plus']"
              class="mr-2"
            />Registrarse
          </router-link>
        </template>

        <template v-else>
          <router-link
            to="/dashboard/profile/overview"
            class="font-medium hover:text-[#99b642] transition-colors text-black"
          >
            <font-awesome-icon :icon="['fas', 'user']" class="mr-2" />
            Mi Perfil
          </router-link>
          
          <router-link
            to="/dashboard/membership/details"
            class="font-medium hover:text-[#99b642] transition-colors text-black"
          >
            <font-awesome-icon :icon="['fas', 'id-card']" class="mr-2" />
            Mi Membresía
          </router-link>
          
          <router-link
            to="/dashboard/events/my-events"
            class="font-medium hover:text-[#99b642] transition-colors text-black"
          >
            <font-awesome-icon :icon="['fas', 'calendar-check']" class="mr-2" />
            Mis Eventos
          </router-link>
          
          <button
            @click="logout"
            class="font-medium hover:text-red-500 transition-colors text-black flex items-center"
          >
            <font-awesome-icon :icon="['fas', 'sign-out-alt']" class="mr-2" />
            Cerrar Sesión
          </button>
        </template>
      </nav>

      <button
        class="lg:hidden flex items-center text-black"
        @click="toggleMenu"
        aria-label="Menú principal"
      >
        <font-awesome-icon
          :icon="['fas', 'bars']"
          class="text-2xl"
        />
      </button>
    </div>

    <transition
      enter-active-class="transition-all duration-300 ease-in-out"
      enter-from-class="opacity-0 translate-x-full"
      enter-to-class="opacity-100 translate-x-0"
      leave-active-class="transition-all duration-300 ease-in-out"
      leave-from-class="opacity-100 translate-x-0"
      leave-to-class="opacity-0 translate-x-full"
    >
      <div 
        v-if="isMenuOpen" 
        class="fixed inset-0 z-200 lg:hidden flex"
      >
        <div 
          class="fixed inset-0 bg-black bg-opacity-50"
          @click="closeMenu"
        ></div>
        
        <div
          class="fixed inset-y-0 left-0 w-full max-w-sm bg-white z-200 flex flex-col shadow-xl"
        >
          <div
            class="flex justify-between items-start p-5 border-b border-gray-200"
          >
            <img
              src="@/assets/img/hike-variant-2.png"
              alt="Hike Society"
              class="h-24"
            />
            <button
              class="text-gray-800 hover:text-[#99b642] transition-colors"
              @click="closeMenu"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="text-2xl" />
            </button>
          </div>

          <div class="flex-grow overflow-y-auto">
            <nav class="p-6">
              <div v-if="!isLoggedIn" class="space-y-6">
                <router-link
                  to="/eventos"
                  class="flex items-center py-4 text-gray-800 hover:text-[#99b642] transition-colors border-b border-gray-100 text-lg"
                  @click="closeMenu"
                >
                  <font-awesome-icon
                    :icon="['fas', 'calendar-alt']"
                    class="w-8 text-[#99b642]"
                  />
                  <span class="ml-4">Eventos</span>
                </router-link>

                <router-link
                  to="/login"
                  class="flex items-center py-4 text-gray-800 hover:text-[#99b642] transition-colors border-b border-gray-100 text-lg"
                  @click="closeMenu"
                >
                  <font-awesome-icon
                    :icon="['fas', 'sign-in-alt']"
                    class="w-8 text-[#99b642]"
                  />
                  <span class="ml-4">Iniciar sesión</span>
                </router-link>

                <router-link 
                  to="/register" 
                  class="flex items-center justify-center w-full py-3 mt-6 rounded-full bg-[#99b642] text-white font-medium hover:bg-[#89a438] transition-colors"
                  @click="closeMenu"
                >
                  <font-awesome-icon :icon="['fas', 'user-plus']" class="mr-2" />
                  <span>Registrarse</span>
                </router-link>
              </div>

              <div v-else class="space-y-6">
                <div class="py-3 mb-4 border-b border-gray-200">
                  <p class="text-lg text-gray-800 font-medium">{{ userData?.name || 'Mi Cuenta' }}</p>
                  <p class="text-sm text-gray-500 mt-1">{{ userData?.email || '' }}</p>
                </div>

                <router-link
                  to="/dashboard/profile/overview"
                  class="flex items-center py-3 text-gray-800 hover:text-[#99b642] transition-colors border-b border-gray-100 text-lg"
                  @click="closeMenu"
                >
                  <font-awesome-icon
                    :icon="['fas', 'user']"
                    class="w-8 text-[#99b642]"
                  />
                  <span class="ml-4">Mi Perfil</span>
                </router-link>

                <router-link
                  to="/dashboard/membership/details"
                  class="flex items-center py-3 text-gray-800 hover:text-[#99b642] transition-colors border-b border-gray-100 text-lg"
                  @click="closeMenu"
                >
                  <font-awesome-icon
                    :icon="['fas', 'id-card']"
                    class="w-8 text-[#99b642]"
                  />
                  <span class="ml-4">Mi Membresía</span>
                </router-link>

                <router-link
                  to="/dashboard/events/my-events"
                  class="flex items-center py-3 text-gray-800 hover:text-[#99b642] transition-colors border-b border-gray-100 text-lg"
                  @click="closeMenu"
                >
                  <font-awesome-icon
                    :icon="['fas', 'calendar-check']"
                    class="w-8 text-[#99b642]"
                  />
                  <span class="ml-4">Mis Eventos</span>
                </router-link>

                <button
                  @click="logout"
                  class="flex items-center py-3 text-gray-800 hover:text-red-500 transition-colors w-full text-left border-b border-gray-100 text-lg"
                >
                  <font-awesome-icon
                    :icon="['fas', 'sign-out-alt']"
                    class="w-8 text-red-500"
                  />
                  <span class="ml-4">Cerrar Sesión</span>
                </button>
              </div>
            </nav>
          </div>

          <div class="p-6 border-t border-gray-200 bg-gray-50">
            <p class="text-center text-gray-500 text-sm">
            </p>
            <p class="text-center text-gray-400 mt-2 text-xs">
              Versión {{ appVersion }}
            </p>
          </div>
        </div>
      </div>
    </transition>
  </header>

  <div class="h-16 md:h-20"></div>
</template>

<style scoped>
/* Asegura que la transición del header sea suave */
.fixed.top-0 {
  transition: box-shadow 0.3s ease, padding 0.3s ease;
}

/* Efecto hover en los iconos del menú */
.svg-inline--fa {
  transition: transform 0.3s ease;
}

nav a:hover .svg-inline--fa[data-icon="calendar-alt"],
nav a:hover .svg-inline--fa[data-icon="user"],
nav a:hover .svg-inline--fa[data-icon="id-card"],
nav a:hover .svg-inline--fa[data-icon="calendar-check"] {
  transform: translateX(5px);
}

/* Estilos para links activos */
.router-link-active {
  color: #99b642 !important;
  font-weight: 600;
}
.z-200 {
  z-index: 200;
}
</style>